import type { User } from '@auth0/auth0-react';

import { getPathnameFromPath, NAVIGATION_URLS } from '@libs/core';

import { AUTH0_REGISTRATION_CLAIM } from '../../utils/auth0IdToken';
import type { Auth0AppState } from '../../utils/getAuth0LoginRedirectOptions';
import { isSamePageReturnTo, stripTrailingSlashes } from '../../utils/getAuth0ReturnTo';

const DEFAULT_DESTINATION = '/';
const TREATS_GUEST_LANDING_PATH = '/treats-rewards';

/**
 * Matches the legacy create-account landing, `from` flag included. Read lazily: this module
 * is pulled in through the package barrel by consumers that never reach a registration.
 */
function registrationDestination(): string {
    return `${NAVIGATION_URLS.velocity.account.aboutYou}?from=create-account`;
}

export function isAuth0Registration(user?: User): boolean {
    return user?.[AUTH0_REGISTRATION_CLAIM] === true;
}

/**
 * Home and Treats Rewards landing/home are browsing pages, not a committed post-login
 * destination like checkout or a specific offer. New accounts from those pages go to About You.
 */
function isRegistrationLandingPath(returnTo?: string): boolean {
    const pathname = stripTrailingSlashes(getPathnameFromPath(returnTo ?? '')) || DEFAULT_DESTINATION;

    return (
        pathname === DEFAULT_DESTINATION ||
        pathname === TREATS_GUEST_LANDING_PATH ||
        pathname === NAVIGATION_URLS.velocity.account.treatOffers
    );
}

/**
 * Picks the landing page after Auth0 callback.
 *
 * New accounts from a regular header "Sign in / Create account" (implicit `returnTo`) go to
 * About You — Home, Treats landing, PLP, account pages, etc.
 *
 * Cart, checkout, and product pages are committed destinations. Even when the header CTA
 * inferred them as the current page (`returnToIsImplicit`), a new account must return there
 * instead of About You. Explicit `returnTo` for wishlist / a specific offer is also kept.
 *
 * Returning sign-in always keeps `returnTo` (never forced to About You).
 */
export function resolveAuth0Destination(appState?: Auth0AppState, user?: User): string {
    if (
        isAuth0Registration(user) &&
        !isSamePageReturnTo(appState?.returnTo) &&
        (appState?.returnToIsImplicit || isRegistrationLandingPath(appState?.returnTo))
    ) {
        return registrationDestination();
    }

    return appState?.returnTo ?? DEFAULT_DESTINATION;
}
